import styled, { keyframes } from 'styled-components';

import { Text } from '../../../../components';
import { DOCUSIGN, REDUCED_MOTION, SECURE_BRAND, SECURE_BRAND_ANIM, SPACE } from '../../../../theme/tokens';
import { DocusignLogo } from '../DocusignLogo';

/** The email slides up into the well from below its bottom edge. */
const arrive = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SPACE[32]});
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** The button pressed once: in, and back. */
const press = keyframes`
  0%,
  100% {
    transform: none;
  }
  30% {
    transform: scale(0.92);
  }
`;

/** The halo the press sends out round the button, wider than tall so it spreads evenly. */
const halo = keyframes`
  from {
    opacity: 0.9;
    transform: none;
  }
  to {
    opacity: 0;
    transform: scale(1.22, 2.3);
  }
`;

/** The email: a sheet of paper running off the well's bottom edge, so only its top shows. */
export const Mail = styled.div<{ $delayMs: number }>`
  box-sizing: border-box;
  flex: 0 0 auto;
  align-self: flex-start;
  width: 196px;
  /* Starts below the well's top, and runs past its bottom, which clips it. */
  margin-top: ${SPACE[20]};
  padding: ${SPACE[8]} ${SPACE[8]} ${SPACE[0]};
  border: 1px solid ${DOCUSIGN.paperEdge};
  border-radius: 10px;
  background: ${DOCUSIGN.paper};
  filter: ${SECURE_BRAND.paperShadow};
  animation: ${arrive} ${SECURE_BRAND_ANIM.EMAIL_ARRIVE_MS}ms ${SECURE_BRAND_ANIM.EASE} both;
  animation-delay: ${(p) => p.$delayMs}ms;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** DocuSign's logo at the head of the email, centred, in its ink. */
export const Logo = styled(DocusignLogo)`
  margin: ${SPACE[2]} auto ${SPACE[8]};
  font-size: 9px;
  color: ${DOCUSIGN.ink};
`;

/** The violet panel: the pen icon, two lines of copy, the button. */
export const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[12]} ${SPACE[12]} ${SPACE[16]};
  border-radius: 6px;
  background: ${DOCUSIGN.emailPanel};
`;

/** The white tile holding the pen. */
export const HeroIcon = styled.div`
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  margin-bottom: ${SPACE[4]};
  border-radius: 5px;
  background: ${DOCUSIGN.paper};
`;

export const PenIcon = styled.svg`
  display: block;
  width: 14px;
  height: 14px;
  fill: none;
  stroke: ${DOCUSIGN.ink};
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

/** A line of the panel's copy, as a bar. */
export const HeroLine = styled.span<{ $width: number }>`
  display: block;
  width: ${(p) => p.$width}%;
  height: 4px;
  border-radius: 2px;
  background: ${DOCUSIGN.emailPanelLine};
`;

/** Review Document: pressed once, a beat after the email lands, with a halo round it. */
export const MailButton = styled.span<{ $delayMs: number }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  height: 20px;
  margin-top: ${SPACE[8]};
  padding: ${SPACE[0]} ${SPACE[8]};
  border-radius: 4px;
  background: ${DOCUSIGN.emailButton};
  animation: ${press} ${SECURE_BRAND_ANIM.TAP_MS * 0.45}ms ${SECURE_BRAND_ANIM.EASE};
  animation-delay: ${(p) => p.$delayMs + SECURE_BRAND_ANIM.TAP_DELAY_MS}ms;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid ${DOCUSIGN.emailButton};
    border-radius: inherit;
    opacity: 0;
    pointer-events: none;
    animation: ${halo} ${SECURE_BRAND_ANIM.TAP_MS}ms ${SECURE_BRAND_ANIM.EASE};
    animation-delay: ${(p) => p.$delayMs + SECURE_BRAND_ANIM.TAP_DELAY_MS}ms;
  }

  ${REDUCED_MOTION} {
    animation: none;

    &::after {
      animation: none;
    }
  }
`;

/** The button's label, in DocuSign's wording for the app's language. */
export const ButtonLabel = styled(Text)`
  overflow: hidden;
  font-size: 8.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${DOCUSIGN.emailButtonText};
`;

/** The email's body under the panel, as lines. */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]} ${SPACE[4]} ${SPACE[20]};
`;

export const PaperLine = styled.span<{ $width: number }>`
  display: block;
  width: ${(p) => p.$width}%;
  height: 3px;
  border-radius: 2px;
  background: ${DOCUSIGN.line};
`;
